import { useState, useEffect } from 'react';
import axios from 'axios';
import { History, ChevronDown, ChevronUp, User, FileText, FileSignature, Euro, ClipboardList, Upload, Trash2, Edit, CheckCircle, Send, Filter } from 'lucide-react';
import { Button } from '../ui/button';

const ACTION_ICONS = {
  creation: <CheckCircle className="h-4 w-4 text-green-500" />,
  modification: <Edit className="h-4 w-4 text-blue-500" />,
  suppression: <Trash2 className="h-4 w-4 text-red-500" />,
  upload: <Upload className="h-4 w-4 text-indigo-500" />,
  signature: <FileSignature className="h-4 w-4 text-purple-500" />,
  envoi: <Send className="h-4 w-4 text-cyan-500" />,
  validation: <CheckCircle className="h-4 w-4 text-emerald-500" />,
};

const ACTION_LABELS = {
  creation: 'Création',
  modification: 'Modification',
  suppression: 'Suppression',
  upload: 'Upload',
  signature: 'Signature',
  envoi: 'Envoi',
  validation: 'Validation',
};

const ENTITE_LABELS = {
  stagiaire: 'Profil stagiaire',
  document: 'Document',
  convention: 'Convention',
  finance: 'Finance',
  grille_evaluation: 'Grille évaluation',
  presence: 'Présence',
  note: 'Note',
};

const ENTITE_ICONS = {
  stagiaire: <User className="h-4 w-4" />,
  document: <FileText className="h-4 w-4" />,
  convention: <FileSignature className="h-4 w-4" />,
  finance: <Euro className="h-4 w-4" />,
  grille_evaluation: <ClipboardList className="h-4 w-4" />,
};

const HistoriqueModificationsSection = ({ stagiaireId, token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    if (stagiaireId && expanded) {
      loadHistorique();
    }
  }, [stagiaireId, expanded]);

  const loadHistorique = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/historique?entite_id=${stagiaireId}&limit=100`);
      setEntries(res.data.entries || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = filterType 
    ? entries.filter(e => e.entite_type === filterType) 
    : entries;

  const entiteTypes = [...new Set(entries.map(e => e.entite_type))];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="historique-modifications-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
        data-testid="historique-toggle-btn"
      >
        <h2 className="text-lg font-display font-semibold text-[#0F172A] flex items-center gap-2">
          <History className="h-5 w-5 text-slate-500" />
          Historique des modifications
          {entries.length > 0 && (
            <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{entries.length}</span>
          )}
        </h2>
        {expanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-6 pb-5 border-t border-slate-100">
          {/* Filtres */}
          {entiteTypes.length > 1 && (
            <div className="flex items-center gap-2 py-3 flex-wrap" data-testid="historique-filters">
              <Filter className="h-4 w-4 text-slate-400" />
              <button
                onClick={() => setFilterType('')}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${!filterType ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid="historique-filter-all"
              >
                Tout
              </button>
              {entiteTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors flex items-center gap-1 ${filterType === type ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`historique-filter-${type}`}
                >
                  {ENTITE_LABELS[type] || type}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-slate-300 border-t-[#0F172A] rounded-full" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="historique-empty">
              Aucune modification enregistrée
            </p>
          ) : (
            <div className="relative" data-testid="historique-timeline">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-px bg-slate-200" />
              
              <div className="space-y-0">
                {filteredEntries.map((entry, idx) => (
                  <div key={idx} className="relative flex gap-4 py-3 group" data-testid={`historique-entry-${idx}`}>
                    {/* Timeline dot */}
                    <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center group-hover:border-slate-400 transition-colors">
                      {ACTION_ICONS[entry.action] || <Edit className="h-4 w-4 text-slate-400" />}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#0F172A]">
                          {ACTION_LABELS[entry.action] || entry.action}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 flex items-center gap-1">
                          {ENTITE_ICONS[entry.entite_type]}
                          {ENTITE_LABELS[entry.entite_type] || entry.entite_type}
                        </span>
                      </div>
                      
                      {entry.entite_label && (
                        <p className="text-sm text-slate-600 mt-0.5">{entry.entite_label}</p>
                      )}
                      
                      {entry.champs_modifies && entry.champs_modifies.length > 0 && (
                        <p className="text-xs text-slate-400 mt-1">
                          Champs : {entry.champs_modifies.join(', ')}
                        </p>
                      )}
                      
                      {entry.details && Object.keys(entry.details).length > 0 && (
                        <div className="text-xs text-slate-400 mt-1 flex flex-wrap gap-2">
                          {Object.entries(entry.details).map(([k, v]) => (
                            <span key={k} className="bg-slate-50 px-1.5 py-0.5 rounded">
                              {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                        <span>{entry.user_nom || 'Système'}</span>
                        <span>·</span>
                        <span>{entry.user_role}</span>
                        <span>·</span>
                        <span>{formatDate(entry.date)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {entries.length > 0 && (
            <div className="flex justify-center mt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={loadHistorique}
                className="text-xs text-slate-500"
                data-testid="historique-refresh-btn"
              >
                Rafraîchir
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoriqueModificationsSection;
